// Mirayama colour finder: the field is painted only from the ponchos' own colours.
// Each poncho is a point (placed by hue across, lightness down); every pixel blends
// the nearby ponchos' colours in CIELAB, so the field holds nothing we don't make.
// Picking a spot shows the poncho whose point is nearest, plus the next three.
// The field's shape comes from the canvas (width / height), so a landscape or a
// portrait field paints, spaces and picks in true screen distances.
if (!customElements.get('mm-colour-finder')) {

  const hslToRgb = (h, s, l) => {
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
  };
  const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const rgbToHex = (rgb) => '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
  const rgbToHsl = ([r, g, b]) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h *= 60;
    }
    return [h, s, l];
  };
  const rgbToLab = ([r, g, b]) => {
    const lin = (c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    const R = lin(r), G = lin(g), B = lin(b);
    const x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
    const y = R * 0.2126 + G * 0.7152 + B * 0.0722;
    const z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
    const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
    return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
  };

  const labToRgb = ([L, a, b]) => {
    const fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200;
    const inv = (t) => (t ** 3 > 0.008856 ? t ** 3 : (t - 16 / 116) / 7.787);
    const x = inv(fx) * 0.95047, y = inv(fy), z = inv(fz) * 1.08883;
    const R = x * 3.2406 + y * -1.5372 + z * -0.4986;
    const G = x * -0.9689 + y * 1.8758 + z * 0.0415;
    const B = x * 0.0557 + y * -0.204 + z * 1.057;
    const g = (c) => Math.round(255 * Math.min(1, Math.max(0, c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)));
    return [g(R), g(G), g(B)];
  };
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

  customElements.define(
    'mm-colour-finder',
    class extends HTMLElement {
      connectedCallback() {
        this.field = this.querySelector('.mm-cf__field');
        this.canvas = this.querySelector('.mm-cf__canvas');
        this.cursor = this.querySelector('.mm-cf__cursor');
        this.markers = this.querySelector('.mm-cf__markers');
        this.cards = Array.from(this.querySelectorAll('.mm-cf__card'));
        this.nearRow = this.querySelector('.mm-cf__near-row');
        this.hex = this.querySelector('.mm-cf__hex');
        this.swatch = this.querySelector('.mm-cf__swatch');
        this.count = this.querySelector('.mm-cf__count');

        this.items = this.cards
          .map((card) => {
            const c1 = hexToRgb(card.dataset.c1);
            return c1 ? { card, c1, lab: rgbToLab(c1) } : null;
          })
          .filter(Boolean);
        if (!this.items.length) return;
        if (this.count) this.count.textContent = this.items.length + ' ponchos';

        // Show the opening poncho first, so the band (which stretches to the picture's
        // height) has its final shape before it is painted.
        const opener = this.items.find((i) => i.card.dataset.title.includes('Gypset')) || this.items[0];
        opener.card.hidden = false;
        this.fitCanvas();

        // Aspect of the field (width / height), read from the canvas, which the CSS keeps
        // at the same shape. Distances are measured in units of the field's area, so the
        // spacing between ponchos is the same whatever the shape.
        this.setAspect();

        this.layout();
        this.paint();
        this.placeMarkers();

        this.locked = false;
        const fromEvent = (e) => {
          const r = this.field.getBoundingClientRect();
          return {
            x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
            y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
          };
        };
        this.field.addEventListener('pointermove', (e) => {
          if (e.pointerType === 'mouse' && this.locked) return;
          if (e.pointerType !== 'mouse' && e.buttons === 0) return;
          this.pick(fromEvent(e));
        });
        this.field.addEventListener('pointerdown', (e) => {
          this.field.setPointerCapture?.(e.pointerId);
          this.locked = e.pointerType === 'mouse' ? !this.locked : false;
          this.pick(fromEvent(e));
        });
        this.field.addEventListener('keydown', (e) => {
          const step = e.shiftKey ? 0.1 : 0.025;
          const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
          if (!d) return;
          e.preventDefault();
          this.pick({ x: Math.min(1, Math.max(0, this.pos.x + d[0])), y: Math.min(1, Math.max(0, this.pos.y + d[1])) });
        });
        this.markers.addEventListener('click', (e) => {
          const m = e.target.closest('[data-idx]');
          if (m) this.pick(this.items[+m.dataset.idx].p);
        });
        const first = opener;
        this.pick(first.p);

        // Repaint at the new shape when the band changes size (rotation, window resize).
        let t;
        new ResizeObserver(() => {
          clearTimeout(t);
          t = setTimeout(() => {
            if (!this.fitCanvas()) return;
            const best = this.best;
            this.setAspect(); this.layout(); this.paint(); this.placeMarkers();
            this.pick(best ? best.p : first.p);
          }, 150);
        }).observe(this.field);
      }

      // Give the canvas the band's own proportions (about 120 px across), so the spectrum
      // is painted at its true shape instead of being stretched. Returns true if it changed.
      fitCanvas() {
        const r = this.field.getBoundingClientRect();
        if (!r.width || !r.height) return false;
        const w = 120, h = Math.max(120, Math.min(1200, Math.round((w * r.height) / r.width)));
        if (this.canvas.width === w && this.canvas.height === h) return false;
        this.canvas.width = w; this.canvas.height = h;
        return true;
      }

      setAspect() {
        const aspect = this.canvas.width / this.canvas.height || 1;
        this.portrait = aspect < 1;
        this.kx = Math.sqrt(aspect);
        this.ky = 1 / Math.sqrt(aspect);
      }

      // Screen distance between two points of the field, in units of its area.
      dist(ax, ay, bx, by) {
        return Math.hypot((bx - ax) * this.kx, (by - ay) * this.ky);
      }

      // Place each poncho by its own hue along the field's long side and its lightness
      // along the short side, then spread overlapping points apart so every colour
      // gets its own territory.
      layout() {
        const pad = 0.09;
        this.items.forEach((it) => {
          const [h, s, l] = rgbToHsl(it.c1);
          const hue = s < 0.12 ? 0.5 : ((h + 330) % 360) / 360; // start the run at magenta-red
          const along = pad + hue * (1 - 2 * pad);
          const across = pad + (1 - l) * (1 - 2 * pad); // light first, dark last
          it.p = this.portrait ? { x: across, y: along } : { x: along, y: across };
        });
        const min = 0.165;
        for (let iter = 0; iter < 120; iter++) {
          for (let i = 0; i < this.items.length; i++) {
            for (let j = i + 1; j < this.items.length; j++) {
              const a = this.items[i].p, b = this.items[j].p;
              const dx = (b.x - a.x) * this.kx, dy = (b.y - a.y) * this.ky;
              const d = Math.hypot(dx, dy) || 0.001;
              if (d < min) {
                const push = (min - d) / 2;
                const ux = dx / d / this.kx, uy = dy / d / this.ky;
                a.x -= ux * push; a.y -= uy * push; b.x += ux * push; b.y += uy * push;
              }
            }
          }
          this.items.forEach(({ p }) => {
            p.x = Math.min(1 - pad / 2, Math.max(pad / 2, p.x));
            p.y = Math.min(1 - pad / 2, Math.max(pad / 2, p.y));
          });
        }
      }

      // Colour at a point: inverse-distance blend of the ponchos' colours in CIELAB.
      labAt(x, y) {
        let wsum = 0, L = 0, A = 0, B = 0;
        for (const it of this.items) {
          const dx = (x - it.p.x) * this.kx, dy = (y - it.p.y) * this.ky;
          const d2 = dx * dx + dy * dy;
          const w = 1 / Math.pow(d2 + 1e-5, 2.2);
          wsum += w; L += w * it.lab[0]; A += w * it.lab[1]; B += w * it.lab[2];
        }
        return [L / wsum, A / wsum, B / wsum];
      }

      paint() {
        const ctx = this.canvas.getContext('2d');
        const { width: w, height: h } = this.canvas;
        const img = ctx.createImageData(w, h);
        let seed = 7;
        const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const lab = this.labAt(x / (w - 1), y / (h - 1));
            lab[0] += (rand() - 0.5) * 3.2; // faint velvet grain
            const [r, g, b] = labToRgb(lab);
            const i = (y * w + x) * 4;
            img.data[i] = r; img.data[i + 1] = g; img.data[i + 2] = b; img.data[i + 3] = 255;
          }
        }
        ctx.putImageData(img, 0, 0);
      }

      placeMarkers() {
        this.markers.textContent = '';
        this.items.forEach((it, idx) => {
          const m = document.createElement('button');
          m.type = 'button';
          m.className = 'mm-cf__marker';
          m.dataset.idx = idx;
          m.style.left = it.p.x * 100 + '%';
          m.style.top = it.p.y * 100 + '%';
          m.style.background = rgbToHex(it.c1);
          m.tabIndex = -1;
          m.setAttribute('aria-label', it.card.dataset.title);
          this.markers.append(m);
        });
      }

      pick(pos) {
        this.pos = pos;
        const rgb = labToRgb(this.labAt(pos.x, pos.y));
        const scored = this.items
          .map((it) => ({ it, d: this.dist(pos.x, pos.y, it.p.x, it.p.y) }))
          .sort((a, b) => a.d - b.d);
        const best = scored[0].it;
        const same = this.best === best;
        this.best = best;

        this.cursor.style.left = pos.x * 100 + '%';
        this.cursor.style.top = pos.y * 100 + '%';
        this.cursor.style.background = rgbToHex(rgb);
        // Same nearest poncho as before: only the cursor moves, nothing else is rebuilt or announced.
        if (same) return;
        this.field.setAttribute('aria-valuemax', String(this.items.length));
        this.field.setAttribute('aria-valuenow', String(this.items.indexOf(best) + 1));
        this.swatch.style.background = rgbToHex(best.c1);
        this.hex.textContent = best.card.dataset.title + (best.card.dataset.colour ? ' · ' + best.card.dataset.colour : '');
        this.field.setAttribute('aria-valuetext', 'Closest: ' + best.card.dataset.title);
        this.markers.querySelectorAll('.mm-cf__marker').forEach((m) => m.classList.toggle('is-active', this.items[+m.dataset.idx] === best));

        this.cards.forEach((c) => (c.hidden = c !== best.card));
        this.nearRow.textContent = '';
        scored.slice(1, 4).forEach(({ it }) => {
          const a = document.createElement('a');
          a.href = it.card.getAttribute('href');
          a.className = 'mm-cf__near-item';
          const img = it.card.querySelector('img');
          if (img) {
            const clone = img.cloneNode();
            clone.removeAttribute('fetchpriority');
            clone.loading = 'lazy';
            clone.sizes = '(min-width: 990px) 200px, 30vw';
            a.append(clone);
          }
          const label = document.createElement('span');
          label.className = 'mm-tech-label';
          label.textContent = it.card.dataset.title.replace(/^The /, '');
          a.append(label);
          this.nearRow.append(a);
        });
      }
    }
  );
}
